import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { formatReportTypes } from "helpers/function";
import { verifyTokenExpire } from "middleware/verifyToken";
import { verifyPermission } from "helpers/permission";
import appConfig from "config/appConfig";

const endPointConfig = {
  PRO: {
    plan: "PRO",
    list: `${appConfig.apiDomaine}/my/reporting-claim/config`,
  },
  MACRO: {
    holding: {
      list: `${appConfig.apiDomaine}/any/reporting-claim/config`,
    },
    filial: {
      list: `${appConfig.apiDomaine}/my/reporting-claim/config`,
    },
  },
};

export const useConfigRapportData = (plan, userPermissions, id) => {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState({
    institutions: null,
    staffs: [],
    types: [],
    periodData: [],
    existingConfig: null,
  });
  const [error, setError] = useState(null);

  const getEndpoint = useCallback(() => {
    if (plan === "MACRO") {
      if (
        verifyPermission(
          userPermissions,
          "config-reporting-claim-any-institution"
        )
      ) {
        return endPointConfig[plan].holding;
      } else if (
        verifyPermission(
          userPermissions,
          "list-config-reporting-claim-my-institution"
        )
      ) {
        return endPointConfig[plan].filial;
      }
    }
    return endPointConfig[plan];
  }, [plan, userPermissions]);

  const formatStaffData = (staffs) => {
    return staffs.map((staff) => ({
      ...staff,
      label: `${staff.identite.firstname} ${staff.identite.lastname}`,
      value: staff.id,
    }));
  };

  const formatInstitutionData = (institutions) => {
    return institutions?.length
      ? institutions.map((institution) => ({
          value: institution.id,
          label: institution.name,
        }))
      : null;
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!verifyTokenExpire()) return;

      setIsLoading(true);
      setError(null);

      try {
        const endpoint = getEndpoint();

        const createResponse = await axios.get(`${endpoint.list}/create`);
        const { institutions, staffs, types, period } = createResponse.data;

        const newData = {
          institutions: formatInstitutionData(institutions),
          staffs: formatStaffData(staffs),
          types: formatReportTypes(types),
          periodData: period,
          existingConfig: null,
        };

        if (id) {
          const editResponse = await axios.get(`${endpoint.list}/${id}/edit`);
          const { reportingTask, staffs: allStaffs } = editResponse.data;

          newData.existingConfig = {
            ...reportingTask,
            allStaffs: formatStaffData(allStaffs),
            selectedStaffs: formatStaffData(reportingTask.staffs),
          };
        }

        setData(newData);
      } catch (err) {
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [plan, userPermissions, id, getEndpoint]);

  return { data, isLoading, error, endpoint: getEndpoint() };
};
